import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener('change', this.#handleColourChange, { signal });
    this.slideshow?.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #colourTimer;
  #pendingColour;
  #requestRevision = 0;

  get colourMedia() {
    return Array.from(this.querySelectorAll('[data-nacre-media-colour]'));
  }

  get variantPicker() {
    return this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
  }

  #handleColourChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.optionName !== 'Couleur') return;
    if (input.closest('variant-picker') !== this.variantPicker) return;
    clearTimeout(this.#colourTimer);
    const colour = input.value;
    if (colour === this.#pendingColour) {
      this.#pendingColour = undefined;
      return;
    }
    const entry = this.colourMedia.find((item) => item.dataset.nacreMediaColour === colour);
    if (entry) this.slideshow?.select({ id: entry.dataset.mediaId }, undefined, { animate: false });
  };

  #handleSlideSelect = (event) => {
    if (event.target !== this.slideshow || !event.detail.userInitiated) return;
    this.#selectColourForMedia(event.detail.id);
  };

  #selectColourForMedia(id) {
    const colour = this.colourMedia.find((item) => item.dataset.mediaId === String(id))?.dataset.nacreMediaColour;
    const picker = this.variantPicker;
    const input = Array.from(picker?.querySelectorAll('input[type="radio"]') ?? [])
      .find((item) => item.dataset.optionName === 'Couleur' && item.value === colour);
    clearTimeout(this.#colourTimer);
    this.#pendingColour = undefined;
    if (!input || input.checked) return;
    // Immediately show the matching colour, then use the theme's normal variant update.
    // Keeping the other checked options preserves the selected bedding size.
    this.#pendingColour = colour;
    this.#colourTimer = setTimeout(() => {
      if (!this.isConnected) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, 100);
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#colourTimer);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const revision = ++this.#requestRevision;
    event.promise
      .then(({ detail }) => {
        if (!detail?.html || revision !== this.#requestRevision || !this.isConnected) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Same-product colour changes retain the gallery and its open lightbox.
        // The normal picker event still updates price, availability and purchase variant.
        if (newMediaGallery.dataset.productId === this.dataset.productId && this.colourMedia.length) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
    const slide = this.slideshow?.refs.slides?.[event.detail.index];
    this.#selectColourForMedia(slide?.getAttribute('slide-id'));
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
